import { useRecordEvent } from "app/hook";
import { AnalyticsEventType } from "app/types";
import I18n from "common/i18n";
import { getPlanColor } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import parentI18n from "features/parent/i18n";
import { formatPlanLimit } from "features/user/constants";
import {
  usePlan,
  PlanType,
  PlanCategory,
  BillingPeriod,
  PLAN_LIMITS,
  QuotaStatus,
} from "features/subscription";

type PriceDisplay = { price: number; priceString: string; currencyCode: string };

// チャットの CTA カードまで 3 段バケツリレーするので、途中で形がずれないよう型を共有する。
export type PurchaseHandlers = {
  onPurchase: (category: PlanCategory, billingPeriod: BillingPeriod) => void;
  onOpenPurchasePage: () => void;
  getPriceDisplay?: (category: PlanCategory, billingPeriod: BillingPeriod) => PriceDisplay;
};

type Props = {
  isFromUser: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messageContent: any;
  purchase?: PurchaseHandlers;
};

// メールのトークン（PURCHASE_TOKEN_EXPIRES_DAYS）と揃える。
// メールは切れているのにチャットは押せる、という食い違いを避ける。
const EXPIRE_DAYS = 7;

// 有料プラン加入中は購入させない（重複購入の防止）
const PAID_PLANS: PlanType[] = [
  PlanType.STANDARD,
  PlanType.PREMIUM,
  PlanType.BUILDER,
  PlanType.EDUCATION,
];

const PLAN_OF_CATEGORY: Record<PlanCategory, PlanType> = {
  standard: PlanType.STANDARD,
  premium: PlanType.PREMIUM,
};

const PLAN_NAME_KEY: Record<PlanCategory, string> = {
  standard: "MSG_SUBSCRIPTION_REQUEST_CTA_PLAN_STANDARD",
  premium: "MSG_SUBSCRIPTION_REQUEST_CTA_PLAN_PREMIUM",
};

// kids のプラン比較（PLAN_COMPARISON_SECTIONS）と同じ項目・同じ値を出す。
// 値は PLAN_LIMITS から取り、整形も formatPlanLimit を共有するので表示がズレない。
const featuresOf = (category: PlanCategory) => {
  const limits = PLAN_LIMITS[PLAN_OF_CATEGORY[category]];
  const unlimited = parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_UNLIMITED");
  const value = (quota: QuotaStatus, unitKey: string) =>
    quota.unlimited ? unlimited : parentI18n.t(unitKey, { count: quota.limit });

  return [
    {
      label: parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_PROJECTS"),
      value: value(limits.projects, "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_PROJECTS"),
      indent: false,
    },
    {
      label: parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_CHAT"),
      value: value(limits.aiChatDaily, "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_TIMES"),
      indent: false,
    },
    {
      label: parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_IMAGE"),
      value: "",
      indent: false,
    },
    {
      label: parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_IMAGE_COUNT"),
      value: value(limits.aiImageDaily, "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_TIMES"),
      indent: true,
    },
    {
      label: parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_AI_IMAGE_SIMULTANEOUS"),
      value: value(limits.aiImagePerRequest, "MSG_SUBSCRIPTION_REQUEST_CTA_VALUE_SHEETS"),
      indent: true,
    },
    {
      label: parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_FEATURE_STORAGE"),
      value: limits.assets.unlimited
        ? unlimited
        : formatPlanLimit(limits.assets.limit, false, "bytes"),
      indent: false,
    },
  ];
};

// 親子チャットに届く「購入のお願い」CTA カード。
// purchase 付き（保護者視点）はタップで課金導線を起動。無し（子ども視点）は送信済みの静的表示。
export const PurchaseRequestCard = ({ isFromUser, messageContent, purchase }: Props) => {
  const play = usePlaySound();
  const currentPlan = usePlan();
  const handleRecordEvent = useRecordEvent();

  const hasPlan = !!messageContent?.has_plan;
  const planCategory = (messageContent?.plan_category ?? null) as PlanCategory | null;
  const billingPeriod = (messageContent?.billing_period ?? null) as BillingPeriod | null;
  const childName = messageContent?.child_name ?? "";
  const sideMargin = isFromUser ? "ml-1" : "mr-1";

  const requestedAt = messageContent?.requested_at;
  const remainingMs = requestedAt
    ? EXPIRE_DAYS * 24 * 60 * 60 * 1000 - (Date.now() - new Date(requestedAt).getTime())
    : null;
  const isExpired = remainingMs !== null && remainingMs <= 0;
  const isAlreadyActive = PAID_PLANS.includes(currentPlan);
  const disabledReason = isAlreadyActive ? "ALREADY_ACTIVE" : isExpired ? "EXPIRED" : null;

  if (purchase) {
    const { getPriceDisplay } = purchase;

    const handlePurchase = (category: PlanCategory, period: BillingPeriod) => {
      play();
      handleRecordEvent(AnalyticsEventType.SUBSCRIPTION_REQUEST_CTA_PURCHASE_CLICK, {
        plan_category: category,
        billing_period: period,
      });
      btnDelay(() => purchase.onPurchase(category, period));
    };

    // プランごとの内容と購入ボタン。プラン未指定のときは選べるよう2プランとも並べる。
    const planBlock = (category: PlanCategory, periods: BillingPeriod[]) => {
      const color = getPlanColor(PLAN_OF_CATEGORY[category]);
      return (
        <div
          key={category}
          className="flex-col-el gap-2 w-full box-border p-3 rounded-xl border-2 border-solid bg-white"
          style={{ borderColor: color }}
        >
          <p className="type-text type-body leading-tight" style={{ color }}>
            {parentI18n.t(PLAN_NAME_KEY[category])}
          </p>

          <div className="flex-col-el gap-1 w-full">
            {featuresOf(category).map((feature) => (
              <div
                key={feature.label}
                className={`flex-row-el items-center justify-between w-full ${feature.indent ? "pl-3" : ""}`}
              >
                <span className="type-text type-sm text-gray2-100 leading-tight">
                  {feature.indent ? `\u21b3 ${feature.label}` : feature.label}
                </span>
                <span className="type-text type-sm text-textcolor-black leading-tight">
                  {feature.value}
                </span>
              </div>
            ))}
          </div>

          <div className="flex-row-el gap-2 w-full">
            {periods.map((period) => {
              const price = getPriceDisplay?.(category, period);
              const isAnnual = period === "annual";
              const showDeal = isAnnual && periods.length > 1;
              const label = parentI18n.t(
                isAnnual
                  ? "MSG_SUBSCRIPTION_REQUEST_CTA_ANNUAL"
                  : "MSG_SUBSCRIPTION_REQUEST_CTA_MONTHLY",
                { price: price?.priceString ?? "" },
              );
              return (
                <div key={period} className="flex-col-el flex-1 gap-1">
                  {/* バッジの有無でボタンの位置がずれないよう、無い側も同じ要素を透明で置く */}
                  <p className="type-text type-xs text-center leading-tight">
                    <span
                      className="inline-block px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: showDeal ? "#ffa03b" : "transparent",
                        color: showDeal ? "#ffffff" : "transparent",
                      }}
                    >
                      {parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_ANNUAL_DEAL")}
                    </span>
                  </p>
                  <button
                    type="button"
                    disabled={!!disabledReason}
                    onClick={() => handlePurchase(category, period)}
                    className={`type-inherit-font flex-row-el items-center justify-center gap-1 border-0 box-border rounded-xl py-2 text-white transition-transform ${
                      disabledReason
                        ? "self-center px-4 cursor-not-allowed"
                        : "w-full px-2 cursor-pointer active:translate-y-[2px]"
                    }`}
                    style={{
                      backgroundColor: disabledReason ? "#c8ccd4" : color,
                    }}
                  >
                    <span className="material-symbols-outlined type-md text-white shrink-0">
                      {disabledReason ? "block" : "shopping_cart"}
                    </span>
                    <span className="type-text type-sm text-white leading-tight whitespace-nowrap">
                      {disabledReason
                        ? parentI18n.t(`MSG_SUBSCRIPTION_REQUEST_CTA_${disabledReason}_BTN`)
                        : label}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    const planLabel = planCategory ? parentI18n.t(PLAN_NAME_KEY[planCategory]) : "";

    return (
      <div
        className={`flex-col-el gap-3 w-[75%] p-4 rounded-2xl bg-white border-2 border-solid border-blue-100 ${sideMargin}`}
      >
        <div className="flex-row-el items-center gap-2">
          <span className="material-symbols-outlined type-title text-blue-100 shrink-0">
            volunteer_activism
          </span>
          <p className="type-text type-body text-textcolor-black leading-tight">
            {parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_TITLE")}
          </p>
        </div>

        <p className="type-text type-sm text-textcolor-black leading-tight">
          {hasPlan
            ? parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_BODY_WITH_PLAN", {
                name: childName,
                plan: planLabel,
              })
            : parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_BODY_NO_PLAN", { name: childName })}
        </p>

        {hasPlan && planCategory && billingPeriod
          ? planBlock(planCategory, [billingPeriod])
          : (["standard", "premium"] as PlanCategory[]).map((category) =>
              planBlock(category, ["monthly", "annual"]),
            )}

        <p className="type-text type-xs text-gray2-100 leading-tight">
          {parentI18n.t(
            disabledReason
              ? `MSG_SUBSCRIPTION_REQUEST_CTA_${disabledReason}`
              : "MSG_SUBSCRIPTION_REQUEST_CTA_SAFETY_NOTE",
          )}
        </p>

        {!disabledReason && remainingMs !== null && (
          <p className="type-text type-xs text-gray2-100 leading-tight">
            {remainingMs > 24 * 60 * 60 * 1000
              ? parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_REMAINING_DAYS", {
                  days: Math.ceil(remainingMs / (24 * 60 * 60 * 1000)),
                })
              : parentI18n.t("MSG_SUBSCRIPTION_REQUEST_CTA_REMAINING_TODAY")}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex-row-el items-center gap-2 max-w-[75%] p-3 rounded-md bg-green-80 ${sideMargin}`}
    >
      <span className="material-symbols-outlined type-md text-textcolor-black shrink-0">
        volunteer_activism
      </span>
      <p className="type-text type-sm text-textcolor-black leading-tight">
        {I18n.t("MSG_SUBSCRIPTION_REQUEST_CARD_CHILD_STATUS")}
      </p>
    </div>
  );
};
